import OpenInNew from '@mui/icons-material/OpenInNew'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Container, Grid, Link, Stack, Typography } from '@mui/material'
import { CreateCampaignContext, useCreateCampaign } from '~/contexts/CreateCampaignContext'
import {
  AvailableTicketsInputObject,
  CampaignCategory,
  CampaignRequirementOptions,
  CampaignSnsRequirementSnsPostType,
  CampaignSnsRequirementSnsType,
  TicketModelCategory,
  useAvailableTicketsQuery,
} from '~/generated/graphql'
import { useContext, useMemo, useState } from 'react'
import { snackActions } from '~/lib/snackbarHelper'
import { AddNewTicket } from './AddNewTicket'
import { useCampaignPageStepForm } from './useCampaignPageStepForm'
import { CampaignContents } from '~/components/common/CampaignContents'
import { PageContainer } from '~/components/common/PageContainer'
import { CampaignRequirementCompositionOptionsType, CampaignRequirementOptionsType } from '~/lib/types/Campaign'
import { encodeId } from '~/lib/convertId'
import { snsSchema } from '../campaign_input/SNS'
import { z } from 'zod'
import { createFormSchema } from '../campaign_input/state'
import { ZodErrorAlert } from '~/components/common/ErrorAlert'
import { logger } from '~/lib/logger'
import { ConfirmedCheckbox } from '~/components/common/ConfirmedCheckbox'
import { PARTIAL_CP_CONFIRMATION_TITLE, CONFIRMATION_WARNING_MESSAGE } from '~/constants/campaign'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'

export const Confirmation = () => {
  const { campaign } = useContext(CreateCampaignContext)
  const { createCampaign, loading } = useCreateCampaign({
    onCreateCampaign: async (e) => {
      if (e) {
        snackActions.error('エラーが発生しました')
        return
      }
      snackActions.success(`${PARTIAL_CP_CONFIRMATION_TITLE}を送信しました`)
    },
  })
  const { onBack } = useCampaignPageStepForm({
    defaultValues: {},
    commit: () => {},
  })

  const [isAgreed, setIsAgreed] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const input: AvailableTicketsInputObject | undefined = campaign.category
    ? {
        category:
          campaign.category === CampaignCategory.Product ? TicketModelCategory.Remote : TicketModelCategory.Locale,
      }
    : undefined

  const {
    data,
    loading: ticketLoading,
    refetch,
  } = useAvailableTicketsQuery({
    variables: {
      input,
    },
  })

  const hasAvailableTickets =
    data?.viewer.company?.availableTickets.length && data.viewer.company.availableTickets.length > 0
  const submitDisabled = !(isAgreed && isConfirmed && hasAvailableTickets)

  const result = useMemo(() => createFormSchema.safeParse(campaign), [campaign])

  if (!result.success) {
    logger.error(
      new Error('Failed to parse campaign data before confirmation', {
        cause: result.error,
      }),
    )

    return (
      <PageContainer>
        <Box textAlign="center" my={6}>
          <Typography variant="headingL" fontWeight="bold">
            {PARTIAL_CP_CONFIRMATION_TITLE}の確認
          </Typography>
        </Box>
        <Container maxWidth="sm">
          <ZodErrorAlert
            title="エラーが発生しました。時間を置いても改善しない場合は、toridori事務局までお問い合わせください。"
            error={result.error}
          />

          <Stack direction="row" justifyContent="center" alignItems="center" mt={5}>
            <Button variant="outlined" size="large" onClick={onBack}>
              戻る
            </Button>
          </Stack>
        </Container>
      </PageContainer>
    )
  }

  const validated = result.data
  const mappingCpSchema = {
    ...validated,
    companySelectSearchGenre: validated.searchGenre,
    subGenre: validated.searchSubGenreIds.map((id) => encodeId('Campaign', id)),
    campaignSnsRequirements: mapSnsRequirementValues(validated.snsRequirements),
    campaignRequirementOptions: mapCampaignRequirementOptions(
      validated.campaignRequirementOptions,
      validated.campaignRequirementCompositionOptions,
    ),
    schedules: validated.schedules ?? undefined,
    offerStarted: validated.offerStarted?.toISOString(),
    location: {
      name: validated.shopName,
      cityName: validated.cityName,
      streetAddress: validated.streetAddress,
      buildingName: validated.buildingName,
      postalCode: validated.shopPostalCode,
      prefectureCode: validated.shopPref,
      tel: validated.shopTel,
      stationName: validated.nearestStationName,
      // 型を合わせるためのdummy
      id: '',
      createdAt: '',
      updatedAt: '',
    },
  }

  return (
    <PageContainer>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          await createCampaign()
        }}
      >
        <Box textAlign="center" my={6}>
          <Typography variant="headingL" fontWeight="bold">
            {PARTIAL_CP_CONFIRMATION_TITLE}の確認
          </Typography>
        </Box>
        <Container maxWidth="sm" disableGutters>
          <CampaignContents campaign={mappingCpSchema} isCpConfirmationPage={true} />

          <Grid justifyContent="center" mt={8} mb={6}>
            <Stack gap={6}>
              {!ticketLoading && !hasAvailableTickets ? (
                <AddNewTicket onReload={refetch} />
              ) : (
                <Stack gap={1} alignItems="center" sx={{ width: 300 }} mx="auto">
                  <ConfirmedCheckbox
                    checked={isAgreed}
                    onChange={setIsAgreed}
                    label={
                      <Typography variant="labelLJpNormal">
                        <Link href={EXTERNAL_LINKS.PROHIBITED_ITEMS} target="_blank" rel="noopener noreferrer">
                          禁止事項
                          <OpenInNew fontSize="inherit" />
                        </Link>
                        に同意します。
                      </Typography>
                    }
                    sx={{ width: '100%' }}
                  />
                  <Stack alignItems="center">
                    <ConfirmedCheckbox
                      checked={isConfirmed}
                      onChange={setIsConfirmed}
                      label={
                        <Typography variant="labelLJpNormal">
                          {PARTIAL_CP_CONFIRMATION_TITLE}を確認しました。
                        </Typography>
                      }
                      sx={{ width: '100%' }}
                    />
                    <Typography variant="caption">{CONFIRMATION_WARNING_MESSAGE}</Typography>
                  </Stack>
                </Stack>
              )}
              <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap" gap={4}>
                <Button variant="outlined" size="large" onClick={onBack}>
                  戻る
                </Button>
                <LoadingButton
                  variant="contained"
                  size="large"
                  disabled={submitDisabled}
                  type="submit"
                  loading={loading || ticketLoading}
                >
                  キャンペーンを審査に提出する
                </LoadingButton>
              </Stack>
            </Stack>
          </Grid>
        </Container>
      </form>
    </PageContainer>
  )
}

//ダミーで型を合わせる
const mapSnsRequirementValues = (formValues: z.infer<typeof snsSchema>['snsRequirements']) => {
  return Object.entries(formValues)
    .filter(([_, value]) => value.checked)
    .map(([key, value]) => {
      const snsPostTypeList =
        'snsPostTypeList' in value
          ? Object.entries(value.snsPostTypeList)
              .filter(([_, v]) => v)
              .map(([k]) => k as CampaignSnsRequirementSnsPostType)
          : []

      const allTags = value.tagList.flatMap((tl) => tl.tags)
      return {
        id: '',
        requestUrl: value.requestUrl,
        snsPostTypeList: snsPostTypeList,
        snsPostRedirectUrl: 'snsPostRedirectUrl' in value ? value.snsPostRedirectUrl : undefined,
        snsType: key as CampaignSnsRequirementSnsType,
        tagList: value.tagList.map((tl) => ({
          id: '',
          languageType: tl.languageType,
          tags: tl.tags,
          __typename: 'CampaignSnsRequirementTag' as const,
        })),
        postFooter: `PR ${value.requestUrl}\n#${allTags.join(' ')}`,
        __typename: 'CampaignSnsRequirement' as const,
      }
    })
}

const mapCampaignRequirementOptions = (
  campaignRequirementOptions?: CampaignRequirementOptionsType,
  campaignRequirementCompositionOptions?: CampaignRequirementCompositionOptionsType,
): CampaignRequirementOptions[] => {
  const mappedOptions: CampaignRequirementOptions[] = []

  if (campaignRequirementOptions) {
    if (campaignRequirementOptions.DRAFT_CHECK) {
      mappedOptions.push(CampaignRequirementOptions.DraftCheck)
    }
    if (campaignRequirementOptions.SECONDARY_USE) {
      mappedOptions.push(CampaignRequirementOptions.SecondaryUse)
    }
    if (campaignRequirementOptions.SPECIFIED_PR_TEXT) {
      mappedOptions.push(CampaignRequirementOptions.SpecifiedPrText)
    }

    // COMPOSITION_SPECIFICATION が true のとき、campaignRequirementCompositionOptions も追加
    if (campaignRequirementOptions.COMPOSITION_SPECIFICATION && campaignRequirementCompositionOptions) {
      const compositionMapping = {
        COMPOSITION_INFLUENCER_AND_SERVICE: CampaignRequirementOptions.CompositionInfluencerAndService,
        COMPOSITION_USAGE_IN_CONTEXT: CampaignRequirementOptions.CompositionUsageInContext,
        COMPOSITION_PRODUCT_ONLY: CampaignRequirementOptions.CompositionProductOnly,
      }

      const mappedComposition = compositionMapping[campaignRequirementCompositionOptions]
      mappedOptions.push(mappedComposition)
    }
  }

  return mappedOptions
}
